import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BrandWordmark } from "@/components/Brand";
import { Download, FileJson, FileSpreadsheet, LogOut, CreditCard, Loader2 } from "lucide-react";

export default function Settings() {
  const { user, logout } = useAuth();
  const [busy, setBusy] = useState("");

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const res = await api.get(`/export`, { params: { fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "json" ? "solofounder_export.json" : "solofounder_export.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${fmt.toUpperCase()}.`);
    } catch (e) {
      toast.error("Could not export your data.");
    } finally {
      setBusy("");
    }
  };

  const initials = (user?.name || "You").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const openPortal = async () => {
    setBusy("portal");
    try {
      const res = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (res.data?.portal_url) window.location.href = res.data.portal_url;
      else toast.error("Could not open the billing portal.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the billing portal.");
      setBusy("");
    }
  };

  return (
    <div className="max-w-2xl">
      <PageHeader testid="settings-header" title="Settings" subtitle="Your account, your data, always yours." />

      {/* Profile */}
      <Card className="rounded-3xl border-border shadow-soft p-6 sm:p-8 mb-6" data-testid="settings-profile">
        <h2 className="font-heading text-xl mb-5">Account</h2>
        <div className="flex items-center gap-4">
          <Avatar className="h-14 w-14">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback className="bg-secondary/15 text-secondary">{initials}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">{user?.name || "Owner"}</p>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-5 leading-relaxed">
          This is your private workspace. Your data is isolated to your account.
        </p>
      </Card>

      {/* Export */}
      <Card className="rounded-3xl border-border shadow-soft p-6 sm:p-8 mb-6" data-testid="settings-export">
        <div className="flex items-center gap-2 mb-2">
          <Download className="h-5 w-5 text-primary" />
          <h2 className="font-heading text-xl">Export your data</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
          Download everything: tasks, content, ideas, brand, SOPs and reviews. Your data always belongs to you.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => download("json")} disabled={busy === "json"} data-testid="export-json-button" variant="outline" className="rounded-full border-border gap-2">
            <FileJson className="h-4 w-4" /> Export JSON
          </Button>
          <Button onClick={() => download("csv")} disabled={busy === "csv"} data-testid="export-csv-button" variant="outline" className="rounded-full border-border gap-2">
            <FileSpreadsheet className="h-4 w-4" /> Export CSV (.zip)
          </Button>
        </div>
      </Card>

      {/* Billing */}
      <Card className="rounded-3xl border-border shadow-soft p-6 sm:p-8 mb-6" data-testid="settings-billing">
        <div className="flex items-center gap-2 mb-2">
          <CreditCard className="h-5 w-5 text-primary" />
          <h2 className="font-heading text-xl">Subscription</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
          Manage your Work &amp; Keep plan, update your payment method, view invoices or cancel, all through Stripe's secure portal.
        </p>
        <Button onClick={openPortal} disabled={busy === "portal"} data-testid="manage-subscription-button" variant="outline" className="rounded-full border-border gap-2">
          {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Manage subscription
        </Button>
      </Card>

      {/* Brand footer */}
      <Card className="rounded-3xl border-border shadow-soft p-6 sm:p-8 mb-6 bg-primary text-primary-foreground" data-testid="settings-brand">
        <BrandWordmark className="text-xl" />
        <p className="text-primary-foreground/75 text-sm mt-1">The Business Operating System for solo founders.</p>
      </Card>

      <Button onClick={logout} data-testid="settings-logout-button" variant="ghost" className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10 gap-2">
        <LogOut className="h-4 w-4" /> Sign out
      </Button>
    </div>
  );
}
